import type { ReactNode } from 'react';
import SafeAreaPage, { type SafeAreaPageProps } from 'layouts/SafeAreaPage';
import Footer from 'components/Footer';
import PoppinsFont from 'components/utilities/PoppinsFont';

interface Props {
  children: ReactNode;
  /** Props forwarded to SafeAreaPage. Defaults to showing the top nav and hiding the sidebar. */
  safeAreaProps?: Omit<SafeAreaPageProps, 'children'>;
  /** Whether to load the Poppins font. Defaults to true. */
  withPoppinsFont?: boolean;
  /** Whether to render the site footer below the content. Defaults to false. */
  withFooter?: boolean;
  /** Class applied to the `<main>` wrapper. */
  mainClassName?: string;
}

/**
 * Generic page layout: optional Poppins font + SafeAreaPage chrome wrapping
 * content in a `<main>`, with an optional site footer. Opt in per page via
 * `Page.getLayout`. Page-specific `<Head>` meta should live in the screen, not here.
 */
export default function MainLayout({
  children,
  safeAreaProps = { isShowTopNav: true, isHideSidebar: true },
  withPoppinsFont = true,
  withFooter = false,
  mainClassName,
}: Props) {
  return (
    <>
      {withPoppinsFont && <PoppinsFont />}
      <SafeAreaPage {...safeAreaProps}>
        <main className={mainClassName}>{children}</main>
        {withFooter && <Footer />}
      </SafeAreaPage>
    </>
  );
}
